import type { LookupOption } from 'src/app/interfaces/lookup-option';
import type {
  TaxonomyIndustryUseCaseRow,
  TaxonomyUseCaseRow,
  TaxonomyProviderCategoryRow,
} from 'src/app/interfaces/taxonomy';
import type { OrbitExplorerOpenQuery } from './orbit-explorer.types';

export interface OrbitOpenPrefill {
  industryId: number | null;
  useCaseId: number | null;
  providerCategoryId: number | null;
}

export interface OrbitOpenPrefillContext {
  industries: LookupOption[];
  industryUseCases: TaxonomyIndustryUseCaseRow[];
  useCases: TaxonomyUseCaseRow[];
  providerCategories: TaxonomyProviderCategoryRow[];
  categoryOptions: Array<{ id: number; label: string }>;
}

function normalizeLabel(value: string | null | undefined): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function labelMatches(candidate: string | null | undefined, target: string): boolean {
  const normalizedCandidate = normalizeLabel(candidate);
  const normalizedTarget = normalizeLabel(target);
  if (!normalizedCandidate || !normalizedTarget) {
    return false;
  }
  return (
    normalizedCandidate === normalizedTarget ||
    normalizedCandidate.includes(normalizedTarget) ||
    normalizedTarget.includes(normalizedCandidate)
  );
}

function resolveProviderCategoryId(
  ctx: OrbitOpenPrefillContext,
  providerCategoryLabel: string | null,
): number | null {
  if (!providerCategoryLabel?.trim()) {
    return null;
  }

  for (const category of ctx.providerCategories) {
    if (labelMatches(category.name, providerCategoryLabel) || labelMatches(category.key, providerCategoryLabel)) {
      return category.id;
    }
  }

  for (const option of ctx.categoryOptions) {
    const strippedLabel = option.label.replace(/\s\(\d+\)$/, '');
    if (
      labelMatches(strippedLabel, providerCategoryLabel) ||
      labelMatches(option.label, providerCategoryLabel)
    ) {
      return option.id;
    }
  }

  return null;
}

function resolveIndustryId(ctx: OrbitOpenPrefillContext, industryLabel: string | null): number | null {
  if (!industryLabel?.trim()) {
    return null;
  }
  for (const industry of ctx.industries) {
    if (labelMatches(industry.name, industryLabel)) {
      return industry.id;
    }
  }
  return null;
}

function useCaseMatchesOrbitName(
  row: Pick<TaxonomyIndustryUseCaseRow, 'useCaseName'>,
  orbitName: string,
): boolean {
  return labelMatches(row.useCaseName, orbitName);
}

function pickUseCaseId(
  ctx: OrbitOpenPrefillContext,
  candidateUseCaseIds: number[],
  industryId: number | null,
  orbitName: string | null,
): number | null {
  if (candidateUseCaseIds.length === 0) {
    return null;
  }
  if (candidateUseCaseIds.length === 1) {
    return candidateUseCaseIds[0];
  }
  if (orbitName?.trim() && industryId != null) {
    const matched = ctx.industryUseCases.find(
      (row) =>
        row.industryId === industryId &&
        candidateUseCaseIds.includes(row.useCaseId) &&
        useCaseMatchesOrbitName(row, orbitName),
    );
    if (matched) {
      return matched.useCaseId;
    }
  }
  return candidateUseCaseIds[0];
}

function enrichPrefillFromIndustryUseCases(
  ctx: OrbitOpenPrefillContext,
  prefill: OrbitOpenPrefill,
): OrbitOpenPrefill {
  let { industryId, useCaseId, providerCategoryId } = prefill;

  if (industryId == null && useCaseId != null) {
    industryId = ctx.industryUseCases.find((row) => row.useCaseId === useCaseId)?.industryId ?? null;
  }

  return { industryId, useCaseId, providerCategoryId };
}

/** Map explorer open-query to taxonomy ids for the client workbench. */
export function resolveOrbitOpenPrefill(
  openQuery: OrbitExplorerOpenQuery,
  ctx: OrbitOpenPrefillContext,
): OrbitOpenPrefill | null {
  const directUseCaseId = openQuery.useCaseId ?? openQuery.pyramidId ?? null;

  if (openQuery.providerCategoryId != null || directUseCaseId != null || openQuery.industryId != null) {
    const providerCategoryId = openQuery.providerCategoryId ?? null;
    const useCaseId = directUseCaseId;
    const industryId =
      openQuery.industryId ??
      resolveIndustryId(ctx, openQuery.industry) ??
      (useCaseId != null
        ? (ctx.industryUseCases.find((row) => row.useCaseId === useCaseId)?.industryId ?? null)
        : null);

    return enrichPrefillFromIndustryUseCases(ctx, { industryId, useCaseId, providerCategoryId });
  }

  const providerCategoryId = resolveProviderCategoryId(ctx, openQuery.pcat);
  let industryId = resolveIndustryId(ctx, openQuery.industry);

  let useCaseId: number | null = null;
  if (industryId != null && openQuery.orbit) {
    useCaseId =
      ctx.industryUseCases.find(
        (row) => row.industryId === industryId && useCaseMatchesOrbitName(row, openQuery.orbit),
      )?.useCaseId ?? null;
  }

  if (useCaseId == null && industryId != null && openQuery.orbit) {
    const candidates = ctx.industryUseCases
      .filter((row) => row.industryId === industryId)
      .map((row) => row.useCaseId);
    useCaseId = pickUseCaseId(ctx, candidates, industryId, openQuery.orbit);
  }

  if (industryId == null && useCaseId != null) {
    industryId = ctx.industryUseCases.find((row) => row.useCaseId === useCaseId)?.industryId ?? null;
  }

  if (industryId == null && openQuery.industry) {
    industryId = resolveIndustryId(ctx, openQuery.industry);
  }

  if (industryId == null && useCaseId == null && providerCategoryId == null) {
    return null;
  }

  return enrichPrefillFromIndustryUseCases(ctx, { industryId, useCaseId, providerCategoryId });
}

/** Admin playground: provider category only. */
export function resolveOrbitOpenAdminPrefill(
  openQuery: OrbitExplorerOpenQuery,
  ctx: Pick<OrbitOpenPrefillContext, 'categoryOptions' | 'providerCategories'>,
): number | null {
  if (openQuery.providerCategoryId != null) {
    return openQuery.providerCategoryId;
  }
  return resolveProviderCategoryId(
    {
      industries: [],
      industryUseCases: [],
      useCases: [],
      providerCategories: ctx.providerCategories,
      categoryOptions: ctx.categoryOptions,
    },
    openQuery.pcat,
  );
}

export function buildOrbitOpenPrefillKey(openQuery: OrbitExplorerOpenQuery | null | undefined): string | null {
  if (!openQuery) {
    return null;
  }
  if (!openQuery.orbit?.trim() && openQuery.providerCategoryId == null && openQuery.businessCaseEntryId == null) {
    return null;
  }
  return [
    openQuery.bc,
    openQuery.businessCaseName,
    openQuery.businessCaseEntryId,
    openQuery.providerCategoryId,
    openQuery.industryId,
    openQuery.useCaseId ?? openQuery.pyramidId,
    openQuery.industry,
    openQuery.orbit,
    openQuery.pcat,
    openQuery.surface,
    openQuery.source,
  ].join('|');
}

export function isOrbitExplorerDeepLink(openQuery: OrbitExplorerOpenQuery | null | undefined): boolean {
  return openQuery?.providerCategoryId != null || openQuery?.businessCaseEntryId != null;
}

/** Focused workbench chrome — Industry/Solution browse is legacy and no longer a landing. */
export function isOrbitExplorerFocusedOpen(openQuery: OrbitExplorerOpenQuery | null | undefined): boolean {
  return isOrbitExplorerDeepLink(openQuery);
}

/** Hub / home live-map open context. Null when the release has no provider category. */
export function orbitExplorerOpenQueryFromCategory(input: {
  providerCategoryId?: number | null;
  orbitLabel?: string | null;
  categoryLabel?: string | null;
}): OrbitExplorerOpenQuery | null {
  const providerCategoryId = input.providerCategoryId;
  if (providerCategoryId == null || !Number.isFinite(providerCategoryId)) {
    return null;
  }
  const categoryLabel = input.categoryLabel?.trim() || null;
  const orbit = input.orbitLabel?.trim() || categoryLabel || 'Orbit';
  return {
    bc: null,
    businessCaseName: null,
    businessCaseEntryId: null,
    providerCategoryId,
    useCaseId: null,
    pyramidId: null,
    industryId: null,
    industry: null,
    orbit,
    lens: 'generalist',
    pcat: categoryLabel || orbit,
    surface: 'orbit',
    source: 'orbit-explorer',
  };
}
